import { useNav } from "context/navContext";
import { useEffect, useState } from "react";

const LETTERS_LAYOUT = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "{bksp}"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p", "-", "_"],
  ["{caps}", "a", "s", "d", "f", "g", "h", "j", "k", "l", "'", ","],
  ["{shift}", "z", "x", "c", "v", "b", "n", "m", ".", "@", "*", "{shift}"],
  ["{clear}", "{space}", "{next}"],
];

// const THEME1 = {
//   bg: "#dcdcdd",
//   key: "#323332",
//   func: "#323332",
//   text: "#ffffff",
//   disabled: "#32333280",
//   accent: "#fe7f04",
// };

const THEME1 = {
  bg: "var(--card)",
  key: "var(--background)",
  func: "var(--background)",
  accent: "var(--accent)",
  text: "#000000",
  disabled: "#E5E7F080",
};

const COLORS = THEME1;

const FUNCTION_LABELS = {
  "{bksp}": "⌫",
  "{clear}": "Clear",
  "{shift}": "⇧",
  "{space}": "␣",
  "{caps}": "⇪",
};

export default function VirtualKeyboard() {
  const { setIsKeyboardOpen } = useNav();

  const [visible, setVisible] = useState(false);
  const [caps, setCaps] = useState(false);
  const [autoShift, setAutoShift] = useState(true);
  const [manualShiftActive, setManualShiftActive] = useState(false);
  const [pressedKey, setPressedKey] = useState(null);
  const [activeEl, setActiveEl] = useState(null);

  const getFocusableInputs = () => {
    if (!activeEl) return [];
    const formContainer = activeEl.closest("form") || document.body;
    return Array.from(
      formContainer.querySelectorAll(
        "input, textarea, [tabindex]:not([tabindex='-1'])"
      )
    ).filter((el) => !el.disabled && !el.readOnly && el.offsetParent !== null);
  };

  const isLastInput = () => {
    const inputs = getFocusableInputs();
    return inputs.indexOf(activeEl) === inputs.length - 1;
  };

  const handleNext = () => {
    const inputs = getFocusableInputs();
    const currentIndex = inputs.indexOf(activeEl);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + 1;
    if (nextIndex < inputs.length) {
      inputs[nextIndex].focus();
    } else {
      if (activeEl) {
        activeEl.blur();
      }
      setVisible(false);
      setIsKeyboardOpen(false);
      setActiveEl(null);
    }
  };

  const updateAutoShift = () => {
    if (!activeEl) return;
    const caret = activeEl.selectionStart ?? 0;
    const prevChar = caret > 0 ? activeEl.value[caret - 1] : "";
    setAutoShift(caret === 0 || prevChar === " ");
  };

  // Focus/blur handling
  useEffect(() => {
    const handleFocus = (e) => {
      const target = e.target;

      const isReactSelectInput =
        target?.getAttribute("role") === "combobox" ||
        (target.id && target.id.startsWith("react-select-")) ||
        (target.className && target.className.includes("react-select"));

      if (
        target &&
        !isReactSelectInput &&
        ["INPUT", "TEXTAREA"].includes(target.tagName) &&
        !["checkbox", "radio"].includes(target.type)
      ) {
        setActiveEl(target);
        setVisible(true);
        setIsKeyboardOpen(true);
        setTimeout(updateAutoShift, 0);
      }
    };

    const handleBlur = () => {
      setTimeout(() => {
        if (
          !document.activeElement ||
          document.activeElement === document.body
        ) {
          setVisible(false);
          setIsKeyboardOpen(false);
          setActiveEl(null);
        }
      }, 100);
    };

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleBlur);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleBlur);
    };
  }, [activeEl]);

  // Highlight physical keyboard presses
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (key === "shift") {
        setManualShiftActive(true);
        return;
      }
      if (key === "capslock") {
        setCaps((prev) => !prev);
        return;
      }
      setPressedKey(key);
      setTimeout(() => setPressedKey(null), 200);
    };

    const handleKeyUp = (e) => {
      if (e.key.toLowerCase() === "shift") setManualShiftActive(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const handleKeyPress = (key) => {
    if (!activeEl) return;

    const type = activeEl.type;
    const isNumberInput = type === "number" || type === "tel";

    if (isNumberInput && /^[a-zA-Z@.]$/.test(key)) return;
    if (
      activeEl.maxLength > 0 &&
      activeEl.value.length >= activeEl.maxLength &&
      !["{bksp}", "{clear}"].includes(key)
    )
      return;

    activeEl.focus();
    setPressedKey(key);
    setTimeout(() => setPressedKey(null), 200);

    let start = activeEl.selectionStart ?? 0;
    let end = activeEl.selectionEnd ?? 0;
    let newValue = activeEl.value;
    const lowerKey = key.toLowerCase();
    let caretPos = start;

    // Handle function keys
    if (lowerKey === "{shift}") {
      if (autoShift) {
        setAutoShift(false);
        return;
      }
      setManualShiftActive((prev) => !prev);
      return;
    }
    if (lowerKey === "{caps}") {
      setCaps((prev) => !prev);
      return;
    }

    // Normal keys
    if (lowerKey === "{bksp}") {
      if (start === end) caretPos = Math.max(0, start - 1);
      newValue =
        start === end
          ? activeEl.value.slice(0, Math.max(0, start - 1)) +
            activeEl.value.slice(end)
          : activeEl.value.slice(0, start) + activeEl.value.slice(end);
    } else if (lowerKey === "{clear}") {
      newValue = "";
      caretPos = 0;
    } else if (lowerKey === "{space}") {
      newValue =
        activeEl.value.slice(0, start) + " " + activeEl.value.slice(end);
      caretPos = start + 1;
    } else if (key.length === 1 || key.match(/^[a-z0-9.@]$/i)) {
      const shouldUpper = caps || manualShiftActive || autoShift;
      const finalKey = shouldUpper ? key.toUpperCase() : key.toLowerCase();
      newValue =
        activeEl.value.slice(0, start) + finalKey + activeEl.value.slice(end);
      caretPos = start + 1;

      if (manualShiftActive) setManualShiftActive(false);
    }

    const nativeSetter = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(activeEl),
      "value"
    )?.set;
    if (nativeSetter) {
      nativeSetter.call(activeEl, newValue);
      activeEl.dispatchEvent(new Event("input", { bubbles: true }));
    }

    setTimeout(() => {
      activeEl.setSelectionRange(caretPos, caretPos);
      updateAutoShift();
    }, 0);
  };

  if (!visible) return null;

  const isNumberInput = activeEl?.type === "number" || activeEl?.type === "tel";
  const showUppercase = caps || manualShiftActive || autoShift;

  return (
    <div
      className="virtual-keyboard fixed right-0 bottom-0 left-0 flex flex-col gap-2 px-4 py-4 select-none"
      style={{ backgroundColor: COLORS.bg, zIndex: 9999 }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {LETTERS_LAYOUT.map((row, i) => (
        <div key={i} className="flex w-full justify-between gap-2">
          {row.map((key, index) => {
            const isFunc = key.startsWith("{") && key.endsWith("}");
            let label =
              FUNCTION_LABELS[key] ?? (showUppercase ? key.toUpperCase() : key);
            if (key === "{next}") label = isLastInput() ? "Continue" : "Next";

            const isActive =
              (key === "{shift}" && manualShiftActive) ||
              (key === "{caps}" && caps) ||
              key === pressedKey;

            const isFunctionKey = key.startsWith("{") && key.endsWith("}");
            const disabled =
              isNumberInput && !isFunctionKey && !/^[0-9]$/.test(key);
            const bgColor =
              key === "{next}"
                ? COLORS.accent
                : disabled
                ? COLORS.disabled
                : isActive
                ? COLORS.accent
                : isFunc
                ? COLORS.func
                : COLORS.key;

            const flexSize =
              key === "{space}"
                ? 5
                : ["{bksp}", "{clear}", "{next}"].includes(key)
                ? 2
                : 1;
            const heightSize = [
              "{space}",
              "{bksp}",
              "{clear}",
              "{next}",
            ].includes(key)
              ? "81px"
              : "auto";
            const aspect = ["{space}", "{bksp}", "{clear}", "{next}"].includes(
              key
            )
              ? "unset"
              : "1 / 1";
            const minWidth = [
              "{space}",
              "{bksp}",
              "{clear}",
              "{next}",
            ].includes(key)
              ? "6rem"
              : "auto";

            return (
              <button
                key={`${key}-${index}`}
                disabled={disabled}
                onClick={(e) => {
                  if (disabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  key === "{next}" ? handleNext() : handleKeyPress(key);
                }}
                className="flex items-center justify-center font-semibold select-none transition-all"
                style={{
                  flex: flexSize,
                  aspectRatio: aspect,
                  height: heightSize,
                  minWidth: minWidth,
                  minHeight: "80px",
                  borderRadius:
                    i === LETTERS_LAYOUT.length - 1 || key === "{bksp}"
                      ? "60px"
                      : "50%",
                  backgroundColor: bgColor,
                  color: COLORS.text,
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                  transition:
                    "background-color 0.15s ease, transform 0.1s ease",
                  fontSize: "24px",
                  border: "none",
                  pointerEvents: disabled ? "none" : "auto",
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isActive)
                    e.currentTarget.style.backgroundColor = COLORS.accent;
                }}
                onMouseLeave={(e) => {
                  if (!disabled && !isActive)
                    e.currentTarget.style.backgroundColor = bgColor;
                }}
                onMouseDown={(e) => {
                  if (!disabled)
                    e.currentTarget.style.transform = "scale(0.95)";
                }}
                onMouseUp={(e) => {
                  if (!disabled) e.currentTarget.style.transform = "scale(1)";
                }}
                onTouchStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
              >
                {label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
